// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * A few measures of the building element picked in 3D (frontend-r22.27),
 * read from the rows the Ground already has: a wall's thickness, height and
 * length, a space's or slab's area, an opening's width and height. Danish,
 * formatted. Pure: no React, no DOM.
 */

import type { BuildingElement, SolidOpening, SolidSlab, SolidSpace, SolidWall } from '../api/types';
import { formatLength } from './ruler';
import { formatAreaDa } from './spaces';

export interface ElementRows {
  walls: readonly SolidWall[] | null | undefined;
  openings: readonly SolidOpening[] | null | undefined;
  spaces: readonly SolidSpace[] | null | undefined;
  slabs: readonly SolidSlab[] | null | undefined;
}

export function elementDetails(element: Pick<BuildingElement, 'guid' | 'category'>, rows: ElementRows): string[] {
  switch (element.category) {
    case 'wall': {
      const w = rows.walls?.find((r) => r.guid === element.guid);
      if (!w) return [];
      const out: string[] = [];
      if (w.thickness !== null && w.thickness > 0) out.push(`Tykkelse ${formatLength(w.thickness)}${w.assumed ? ' (antaget)' : ''}`);
      out.push(`Højde ${formatLength(w.height)}`);
      out.push(`Længde ${formatLength(Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]))}`);
      if (w.is_external) out.push('Ydervæg');
      return out;
    }
    case 'space': {
      const s = rows.spaces?.find((r) => r.guid === element.guid);
      return s && s.area !== null ? [formatAreaDa(s.area), `Højde ${formatLength(s.z_ceiling - s.z_floor)}`] : [];
    }
    case 'slab':
    case 'roof': {
      const s = rows.slabs?.find((r) => r.guid === element.guid);
      return s ? [formatAreaDa(s.area), `Tykkelse ${formatLength(s.z_top - s.z_base)}`] : [];
    }
    default: {
      const o = rows.openings?.find((r) => r.guid === element.guid);
      return o ? [`Bredde ${formatLength(Math.abs(o.s_end - o.s_start))}`, `Højde ${formatLength(o.z_head - o.z_sill)}`] : [];
    }
  }
}
